import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import { useStorageQuota } from "../hooks/useStorageQuota";
import { useState, useEffect, useRef } from "react";
import { auth } from "../lib/firebase";
import { signOut } from "firebase/auth";
import { ChevronDown, Crown, Settings, Gem, CreditCard, LogOut, User, HardDrive, Plus, Menu, FileText, Sparkles } from "lucide-react";
import AuthModal from "./AuthModal";

interface NavbarProps {
  transparent?: boolean;
  logoType?: 'course' | 'default';
}

export default function Navbar({ transparent = true, logoType = 'default' }: NavbarProps) {
  const { user } = useAuthContext();
  const { plan, tier } = useSubscription();
  const { usedGB, limitGB, usagePercent, loading: storageLoading } = useStorageQuota();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showExploreDropdown, setShowExploreDropdown] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const exploreDropdownRef = useRef<HTMLDivElement>(null);

  // Check if user can use Add-ons (Pro tier)
  const canUseAddons = (tier === "pro" || tier === "pro_plus" || tier === "yearly") && plan?.features?.allowAddons === true;

  // Check Salepage access: Pro+ Yearly or admin-enabled
  const hasSalePageAccess = 
    tier === 'pro_plus_yearly' || 
    ((user as any)?.salePageSubscription?.status === 'active');

  // Detect domain for logo selection
  const hostname = window.location.hostname;
  const isCoursedeeDomain = hostname.includes('aiforyou');

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowDropdown(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (exploreDropdownRef.current && !exploreDropdownRef.current.contains(event.target as Node)) {
        setShowExploreDropdown(false);
      }
    };

    if (showDropdown || showExploreDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, showExploreDropdown]);

  return (
    <>
      <nav className={`w-full ${transparent ? 'bg-gradient-to-r from-slate-900 via-blue-900 to-cyan-900 text-white shadow-xl border-b border-blue-500/20' : 'bg-white/80 backdrop-blur-md border-b border-border shadow-soft'} sticky top-0 z-[100]`}>
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          {/* Logo */}
          {isCoursedeeDomain ? (
            <a href="/" className="flex items-center gap-3 group">
              <img
                src="/logo-edumi-course.jpg"
                alt="AIForYou Logo"
                className="h-12 w-auto transform group-hover:scale-105 transition-all duration-300"
              />
            </a>
          ) : (
            <Link to={(user?.role === "instructor" || user?.role === "admin") ? "/instructor/my-courses" : "/"} className="flex items-center gap-3 group">
              <img
                src="/logo-edumi-ai.jpg"
                alt="EduMi Logo"
                className="h-12 w-auto transform group-hover:scale-105 transition-all duration-300"
              />
            </Link>
          )}

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                {/* Admin Dashboard */}
                {(user.email === "viitamint@gmail.com" || user.role === "admin") && (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Crown className="w-4 h-4" />
                  Admin
                </Link>
              )}
              
              {/* Hide Pricing menu for existing users - only show for brand new users */}
              
              {/* Dashboard */}
              {(user.role === "instructor" || user.role === "admin") && (
                <Link
                  to="/instructor/dashboard"
                  className={`font-medium transition-colors duration-200 ${transparent ? 'text-white hover:text-white/80' : 'text-textLight hover:text-primary'}`}
                >
                  Dashboard
                </Link>
              )}
              
              {/* My Features Dropdown */}
              {(user.role === "instructor" || user.role === "admin") && (
                <div className="relative" ref={exploreDropdownRef}>
                  <button
                    onClick={() => setShowExploreDropdown(!showExploreDropdown)}
                    className={`flex items-center gap-2 font-medium transition-colors duration-200 ${transparent ? 'text-white hover:text-white/80' : 'text-textLight hover:text-primary'}`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>ฟีเจอร์ของฉัน</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showExploreDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showExploreDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      {/* คอร์สเรียน - Always show */}
                      <button
                        onClick={() => {
                          setShowExploreDropdown(false);
                          navigate('/instructor/dashboard');
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-cyan-50 transition-colors flex items-center gap-3 text-gray-700"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">คอร์สเรียน</div>
                          <div className="text-xs text-gray-500">จัดการคอร์สเรียน</div>
                        </div>
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </button>

                      {/* Salepage entry removed — Sale Page lives on saledi.web.app, separated from edumi.co */}
                    </div>
                  )}
                </div>
              )}
              
              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className={`flex items-center gap-2 font-medium transition-colors duration-200 ${transparent ? 'text-white hover:text-white/80' : 'text-textLight hover:text-primary'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${transparent ? 'bg-white/20' : 'bg-gradient-primary'}`}>
                    {user.email?.[0].toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm text-textDark font-semibold">{user.name || user.email}</p>
                      <p className="text-xs text-textLight mt-1">{user.email}</p>
                      <p className="text-xs text-textLight mt-1">
                        {user.role === "admin" ? "ผู้ดูแลระบบ" : user.role === "instructor" ? "ผู้สอน" : "ผู้เรียน"} • {plan?.name || "FREE"}
                      </p>
                    </div>

                    {/* Storage Usage Bar */}
                    {!storageLoading && (
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <HardDrive className="w-4 h-4 text-cyan-600" />
                          <p className="text-xs font-semibold text-textDark">พื้นที่จัดเก็บ</p>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-textLight">
                            {usedGB.toFixed(2)} / {limitGB} GB
                          </span>
                          <span className="text-xs font-semibold text-cyan-600">
                            {usagePercent.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              usagePercent > 100 ? 'bg-red-500' : 
                              usagePercent > 80 ? 'bg-yellow-500' : 
                              'bg-gradient-to-r from-cyan-500 to-fuchsia-500'
                            }`}
                            style={{ width: `${Math.min(usagePercent, 100)}%` }}
                          ></div>
                        </div>
                        
                        {/* Upgrade or Add-on Storage Button based on plan */}
                        {tier === "free" && (
                          <Link
                            to="/subscription/renew"
                            className="mt-2 flex items-center gap-1.5 text-xs text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>อัพเกรด</span>
                          </Link>
                        )}
                        {tier && tier !== "free" && (tier.includes("pro") || tier.includes("yearly")) && (
                          <Link
                            to="/subscription/storage-addon"
                            className="mt-2 flex items-center gap-1.5 text-xs text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                            onClick={() => setShowDropdown(false)}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>เพิ่มพื้นที่จัดเก็บ</span>
                          </Link>
                        )}
                      </div>
                    )}

                    <Link
                      to="/instructor/settings"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-textDark hover:bg-cyan-50 transition-colors"
                      onClick={() => setShowDropdown(false)}
                    >
                      <Settings className="w-4 h-4" />
                      ตั้งค่าโปรไฟล์
                    </Link>
                    <Link
                      to="/subscription/renew"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-textDark hover:bg-cyan-50 transition-colors"
                      onClick={() => setShowDropdown(false)}
                    >
                      <Gem className="w-4 h-4" />
                      จัดการ Subscription
                    </Link>
                    <Link
                      to="/payment/history"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-textDark hover:bg-cyan-50 transition-colors"
                      onClick={() => setShowDropdown(false)}
                    >
                      <CreditCard className="w-4 h-4" />
                      ประวัติการชำระเงิน
                    </Link>

                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        ออกจากระบบ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login?redirect=/"
                className={`font-medium transition-colors duration-200 ${transparent ? 'text-white hover:text-white/80' : 'text-textLight hover:text-primary'}`}
              >
                เข้าสู่ระบบ
              </Link>
              <button 
                onClick={() => setShowRegisterModal(true)}
                className="bg-gradient-primary hover:shadow-medium text-white px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                สมัครใช้งาน
              </button>
            </>

          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className={`p-2 transition-colors ${transparent ? 'text-white hover:text-white/80' : 'text-textDark hover:text-primary'}`}
            aria-label="Menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>
    </nav>

    {/* Mobile Menu - OUTSIDE nav element */}
    {showMobileMenu && (
      <div className="md:hidden">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/60"
          style={{ zIndex: 2147483646 }}
          onClick={() => setShowMobileMenu(false)}
        />
        
        {/* Bottom Sheet */}
        <div 
          className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl"
          style={{ 
            zIndex: 2147483647,
            maxHeight: '80vh',
            animation: 'slideUpFromBottom 0.3s ease-out'
          }}
        >
          {/* Handle Bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto px-4 pb-8" style={{ maxHeight: 'calc(80vh - 40px)' }}>
            {user ? (
              <div className="space-y-2">
                {/* User Info Card */}
                <div className="bg-gradient-primary text-white rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                      {user.email?.[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{user.name || user.email}</p>
                      <p className="text-xs opacity-90 truncate">{user.email}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {user.role === "admin" ? "Admin" : user.role === "instructor" ? "ผู้สอน" : "ผู้เรียน"} • {plan?.name || "FREE"}
                      </p>
                    </div>
                  </div>
                  
                  {/* Storage Info - Mobile */}
                  {!storageLoading && (user.role === "instructor" || user.role === "admin") && (
                    <div className="mt-3 pt-3 border-t border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <HardDrive className="w-4 h-4" />
                        <p className="text-xs font-semibold">พื้นที่จัดเก็บ</p>
                      </div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs opacity-90">
                          {usedGB.toFixed(2)} / {limitGB} GB
                        </span>
                        <span className="text-xs font-semibold">
                          {usagePercent.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            usagePercent > 100 ? 'bg-red-500' : 
                            usagePercent > 80 ? 'bg-yellow-500' : 
                            'bg-white'
                          }`}
                          style={{ width: `${Math.min(usagePercent, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Menu Items */}
                {(user.email === "viitamint@gmail.com" || user.role === "admin") && (
                  <Link
                    to="/admin"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 p-4 bg-cyan-600 text-white rounded-xl active:bg-cyan-700"
                  >
                    <Crown className="w-5 h-5" />
                    <span className="font-medium">Admin Dashboard</span>
                  </Link>
                )}

                {(user.role === "instructor" || user.role === "admin") && (
                  <>
                    <Link
                      to="/instructor/my-courses"
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl active:bg-gray-100"
                    >
                      <User className="w-5 h-5 text-cyan-600" />
                      <span className="font-medium">คอร์สของฉัน</span>
                    </Link>

                    {plan?.features?.hasDashboard && (
                      <Link
                        to="/instructor/dashboard"
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl active:bg-gray-100"
                      >
                        <Settings className="w-5 h-5 text-cyan-600" />
                        <span className="font-medium">Dashboard</span>
                      </Link>
                    )}

                    <Link
                      to="/instructor/settings"
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl active:bg-gray-100"
                    >
                      <Settings className="w-5 h-5 text-cyan-600" />
                      <span className="font-medium">ตั้งค่า</span>
                    </Link>

                    <Link
                      to="/subscription/renew"
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl active:bg-gray-100"
                    >
                      <Gem className="w-5 h-5 text-cyan-600" />
                      <span className="font-medium">Subscription</span>
                    </Link>
                  </>
                )}

                <button
                  onClick={() => {
                    handleLogout();
                    setShowMobileMenu(false);
                  }}
                  className="flex items-center gap-3 w-full p-4 bg-red-50 text-red-600 rounded-xl active:bg-red-100 mt-4"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">ออกจากระบบ</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl active:bg-gray-100"
                >
                  <User className="w-5 h-5 text-cyan-600" />
                  <span className="font-medium">เข้าสู่ระบบ</span>
                </Link>
                <button 
                  onClick={() => {
                    setShowMobileMenu(false);
                    setShowRegisterModal(true);
                  }}
                  className="w-full bg-gradient-primary text-white p-4 rounded-xl font-semibold active:opacity-90 mt-2"
                >
                  สมัครใช้งาน
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Register Modal */}
    <AuthModal
      isOpen={showRegisterModal}
      onClose={() => setShowRegisterModal(false)}
      mode="register"
      themeColor="#8B5CF6"
      signupSource="course"
    />
  </>
);
}

